import React, { useState, useEffect, useCallback, useContext } from 'react';
import ImageAnnotationsEditor from '../../../components/images/ImageAnnotationsEditor';
import { IconButton } from '../../../components/common/IconButton';
import TopBarLayout from '../../../components/common/layout/TopBarLayout';
import SingleTextEditor from '../../../components/common/editors/SingleTextEditor';
import LegoAdminPageContext from '../../legoAdminPageContext';
import AsyncS3Uploader from '../../../components/common/react-s3-upload/AsyncS3Uploader';
import { getImageThumbnail } from '../../../../../lib/imageUtilsESM';
import _, { words } from 'lodash';
import { Icon } from '../../../components/common/Icon';
import TextDiff from '../../../components/test-sets/TextDiff';
import MultiButtonSwitch from '../../../components/common/editors/MultiButtonSwitch';
import BtnSeeDiff from '../../../components/common/editors/BtnSeeDiff';
import { calculateOpaqueBounds, fastMedianFilterAproximationWithThreshold } from '../../../../../lib/image-processing/median-aproximation.mjs';
import FactoidImageCameraSelector from './FactoidImageCameraSelector';


/**
 * Creates a cropped PNG File object from an image URL and normalized crop box.
 * Handles various input image formats supported by the browser (like PNG, JPEG, WebP, AVIF).
 * The output is always a PNG File with transparency preserved/supported.
 *
 * @param {string} imageUrl The URL of the source image.
 * @param {object} cropBox An object with { left, top, width, height } normalized (0-1) coordinates.
 * @param {string} [outputFilename='cropped-image.png'] The desired filename for the output File object.
 * @returns {Promise<{file: File, width: number, height: number}>} A Promise that resolves with the cropped PNG File object.
 * @throws {Error} Rejects the promise if the image fails to load or cropping fails.
 */
function createCroppedPngFile(imageUrl, cropBox, outputFilename = 'cropped-image.png') {
    return new Promise((resolve, reject) => {
        const img = new Image();

        // IMPORTANT: Set crossOrigin to 'Anonymous' BEFORE setting img.src
        // This is necessary to allow drawing cross-origin images onto the canvas
        // without "tainting" it. The server hosting the image must also send
        // appropriate CORS headers (e.g., Access-Control-Allow-Origin: *)
        img.crossOrigin = 'Anonymous';

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    reject(new Error("Failed to get 2D context from canvas."));
                    return;
                }

                // Calculate pixel dimensions for cropping based on the original image size
                const sourceX = Math.round(cropBox.left * img.naturalWidth);
                const sourceY = Math.round(cropBox.top * img.naturalHeight);
                const sourceWidth = Math.round(cropBox.width * img.naturalWidth);
                const sourceHeight = Math.round(cropBox.height * img.naturalHeight);

                // Basic validation for calculated dimensions
                if (sourceWidth <= 0 || sourceHeight <= 0) {
                    reject(new Error(`Invalid crop dimensions: width=${sourceWidth}, height=${sourceHeight}. Check cropBox values and image dimensions.`));
                    return;
                }

                // Set the canvas size to the exact size of the cropped area
                canvas.width = sourceWidth;
                canvas.height = sourceHeight;

                // Draw the cropped portion of the original image onto the canvas
                // ctx.drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
                // sx, sy, sWidth, sHeight = source rectangle (from original image)
                // dx, dy, dWidth, dHeight = destination rectangle (on canvas)
                ctx.drawImage( img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height );

                // Convert the canvas content to a PNG Blob
                // canvas.toBlob is asynchronous
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error("Canvas toBlob operation failed."));
                            return;
                        }
                        // Create a File object from the Blob
                        const croppedFile = new File([blob], outputFilename, {
                            type: 'image/png', // Explicitly set MIME type to PNG
                            lastModified: Date.now()
                        });
                        resolve({file: croppedFile, width: sourceWidth, height: sourceHeight});
                    },
                    'image/png' // Specify PNG format (supports transparency)
                    // Optional quality argument (0-1) can be added here for PNG,
                    // but it often doesn't have a significant effect like with JPEG.
                );

            } catch (error) {
                reject(new Error(`Error during canvas processing: ${error.message}`));
            }
        };

        img.onerror = (error) => {
            // Provide a more informative error message for common issues
            let message = `Failed to load image from URL: ${imageUrl}.`;
            if (!imageUrl.startsWith('data:') && window.location.origin !== new URL(imageUrl, window.location.origin).origin) {
                message += ' Possible CORS issue. Ensure the server sends Access-Control-Allow-Origin header.';
            } else {
                message += ' Check if the URL is correct and the image exists.';
            }
            reject(new Error(message));
        };

        // Start loading the image by setting its src
        img.src = imageUrl;
    });
}

const CROPBOX_ANNOTATION = { type: 'boundingBox', id: 'cropbox', color: '#BB00AA' };

const VEHICLE_BOX_ANNOTATION = { type: 'boundingBox', id: 'vehicleBox', color: '#0088CC' };

function SpecFactoidImageEditor({ fragment, onChange }) {
    const { page } = useContext(LegoAdminPageContext);

    const [annotations, setAnnotations] = useState([]);
    const [isCalculating, setIsCalculating] = useState(false);
    const [error, setError] = useState(null);
    const [threshold, setThreshold] = useState(3);

    const [originalFactoid, setOriginalFactoid] = useState(_.cloneDeep(fragment?.factoids?.[0]));
    const [unsavedFactoid, setUnsavedFactoid] = useState(_.cloneDeep(originalFactoid));

    const imageUrl = fragment?.text;

    const calculateCropBox = useCallback((ctx, width, height, alphaThreshold) => {
        console.time('Compute crop box from transparent pixels');
        try {

            // Use the function to calculate bounds with the current threshold
            const imgData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
            const bounds = calculateOpaqueBounds(imgData.data, ctx.canvas.width, ctx.canvas.height, alphaThreshold, {extendPixels: 10, median: 1});
            if (bounds) {
                const { left, top, width: cropWidth, height: cropHeight } = bounds;
                
                const initialCropbox = {
                    ...CROPBOX_ANNOTATION, // Use base details
                    left: left / width,
                    top: top / height,
                    width: cropWidth / width,
                    height: cropHeight / height,
                };
                setAnnotations([initialCropbox]);
            } else {
                // Default to full image if no opaque pixels or error
                setAnnotations([{ ...CROPBOX_ANNOTATION, left: 0, top: 0, width: 1, height: 1 }]);
                console.warn("Could not find opaque pixels, defaulting cropbox to full image.");
            }
        } catch (err) {
            console.error("Error processing image for initial cropbox:", err);
            setError(`Error processing image: ${err.message}. Check console and CORS policy.`);
            // Default to full image on error
            setAnnotations([{
                ...CROPBOX_ANNOTATION,
                left: 0, top: 0, width: 1, height: 1
            }]);
        } finally {
            setIsCalculating(false);
            console.timeEnd('Compute crop box from transparent pixels');
        }
    }, []);

    useEffect(() => {
        // Reset state if fragment changes or factoid is present
        setAnnotations([]);
        setError(null);

        if (!imageUrl || unsavedFactoid) {
            return;
        }

        let isMounted = true;
        setIsCalculating(true);

        const img = new Image();
        // Attempt anonymous cross-origin loading for canvas
        img.crossOrigin = "Anonymous";

        img.onload = () => {
            if (!isMounted) return;

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error("Could not get 2D context from canvas");
            }

            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            ctx.drawImage(img, 0, 0);

            calculateCropBox(ctx, img.naturalWidth, img.naturalHeight, threshold);        
        };

        img.onerror = (err) => {
            if (!isMounted) return;
            console.error("Error loading image:", err);
            // Check if it's likely a CORS issue
            const corsError = typeof err === 'string' && err.toLowerCase().includes('cross-origin');
            const message = corsError
                ? "Could not load image due to CORS policy. Canvas analysis requires appropriate headers from the image server."
                : `Failed to load image from ${imageUrl}`;
            setError(message);
            // Provide a default full image box even on load error, so editor is usable
            setAnnotations([{
                ...CROPBOX_ANNOTATION,
                left: 0, top: 0, width: 1, height: 1
            }]);
            setIsCalculating(false);
        };

        img.src = imageUrl;

        return () => {
            isMounted = false; // Cleanup function to prevent state updates on unmounted component
        };

    }, [imageUrl, originalFactoid, threshold]); // Rerun effect if imageUrl, factoid, or threshold changes

    const handleAnnotationsChange = useCallback((newEditorValue) => {
        setAnnotations(newEditorValue.annotations || []);
    }, []);

    const handleUploadCropped = useCallback(() => {
        const cropboxData = annotations.find(a => a.id === 'cropbox');
        if (cropboxData) {
            console.log("Uploading cropped picture (dummy)...");
            console.log("Fragment ID:", fragment?._id);
            console.log("Cropbox Annotation:", cropboxData);

            page.runAsync(async () => {
                const {file, width, height} = await createCroppedPngFile(imageUrl, cropboxData, `spec-${fragment?._id}-cropped.png`);

                const s3LegoImagesService = page.service('services/s3-images-cerokm');

                const getSignedUrl = async (file) => {
                    return await s3LegoImagesService.create({ fileName: file.name, mimeType: file.type, folder: 'spec' })
                }

                const uploadProcess = new AsyncS3Uploader({
                    // preprocess,
                    getSignedUrl,
                    onProgress: (...args) => console.log('Upload progress:', ...args),
                    contentDisposition: 'auto',
                });

                let res = await uploadProcess.uploadFile(file);

                // console.log("Upload result:", res);
                // console.log(getImageThumbnail(res.s3Url.replace('.png', '.avif'), 's'))

                const imageProps = { uri: res.s3Url, width, height };
                let {camera, isRender} = fragment.sourceContext || {};
 
                if(isRender) {
                    imageProps.isRender = isRender;
                }

                let newFactoid = {
                    sourceFragmentId: fragment._id,
                    state: 'complete',
                    derivedSpecs: [
                        [`images.spec.${camera || 'other'}`, imageProps]
                    ],
                    derivedBy: page.getLoggedUserSignature().id
                };

                // setUnsavedFactoid(newFactoid);
                handleSave(newFactoid);

            }, 'Uploading cropped picture...');

        } else {
            console.warn("Cropbox annotation not found.");
            alert("Cropbox annotation not found.");
        }
    }, [annotations, fragment]);

    const handleThresholdChange = useCallback((e) => {
        const newThreshold = parseInt(e.target.value, 10);
        setThreshold(newThreshold);
    }, []);


    const handleSave = (changedFactoid) => {
        const svc = page.service('services/cerokm/sourcefactoid');
        page.runAsync(async () => {
            let res;
            if (changedFactoid._id) {
                // Update existing factoid
                res = await svc.update(changedFactoid._id, changedFactoid);
            } else {
                // Create new factoid
                res = await svc.create(changedFactoid);
            }
            onChange(res);
            console.log("Save factoid response:", res);  
            setOriginalFactoid(res);
            setUnsavedFactoid(_.cloneDeep(res));
        }, 'Saving factoid...');
    };

    if (!unsavedFactoid) {
        if (!imageUrl) {
            return <div className="p-3 border rounded bg-light">No image URL provided in the fragment.</div>;
        }

        const editorValue = {
            url: imageUrl,
            annotations: annotations
        };

        return <div className="position-relative" style={{ width: '100%', height: 'calc(100% - 42px)' }}>
            <TopBarLayout>
                <div className='p-2'>
                    <SingleTextEditor className={'mb-2'} value={imageUrl} small onChange={() => { }} />

                    <div className="text-left d-flex align-items-center">
                        <IconButton
                            icon="cloud_upload"
                            level="success"
                            fill={true}
                            onClick={handleUploadCropped}
                            disabled={isCalculating || error || !annotations.some(a => a.id === 'cropbox')}
                            className="mr-3"
                        >
                            Upload cropped picture
                        </IconButton>

                        <div className="d-flex align-items-center">
                            <label htmlFor="threshold-input" className="mb-0 mr-2">Crop transparency threshold:</label>
                            <input
                                id="threshold-input"
                                type="range"
                                min="1"
                                max="255"
                                value={threshold}
                                onChange={handleThresholdChange}
                                className="form-control-range"
                                style={{ width: "120px" }}
                            />
                            <span className="ml-2">{threshold}</span>
                        </div>

                        {isCalculating && <div className="ml-2 text-center text-muted">Calculating crop box...</div>}

                        {error && <div className="alert alert-danger m-2">{error}</div>}
                    </div>
                </div>

                <ImageAnnotationsEditor key={'pre-upload'} value={editorValue} onChange={handleAnnotationsChange} knownAnnotations={[CROPBOX_ANNOTATION]} />
            </TopBarLayout>
        </div>
    } else {
        const unsavedChanges = !_.isEqual(unsavedFactoid, originalFactoid);

        // if(unsavedChanges) {
        //     page.openModal(<TextDiff inputA={JSON.stringify(factoid, null, 2)} inputB={JSON.stringify(unsavedFactoid, null, 2)} />, {title: 'Changes'});
        // }

        const imageProps = unsavedFactoid.derivedSpecs[0][1];

        const camera = unsavedFactoid.derivedSpecs[0][0].split('.').pop();
        const savedUrl = imageProps.uri;
        const savedAnnotations = imageProps.annotations;

        const setCamera = (newCamera) => {
            unsavedFactoid.derivedSpecs[0][0] = `images.spec.${newCamera}`;
            setUnsavedFactoid({ ... unsavedFactoid });
        }

        const editorValue = {
            url: savedUrl,
            annotations: savedAnnotations
        };

        const factoidAnnotationChange = (updatedImage) => {
            imageProps.annotations = updatedImage.annotations.map(a => _.omit(a, 'color'));
            setUnsavedFactoid({ ... unsavedFactoid });
        };

        const checkVehicleBox = (imageElement) => {
            // The processing is symchronouse, so avoid freezing the UI before react updates everything
            setTimeout(() => {
                if (!savedAnnotations?.length) {
                    const { naturalWidth, naturalHeight } = imageElement;
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        throw new Error("Could not get 2D context from canvas");
                    }

                    canvas.width = naturalWidth;
                    canvas.height = naturalHeight;
                    ctx.drawImage(imageElement, 0, 0);

                    const median = 4;
                    console.time('Compute crop box from transparent pixels with median ' + median);
                    const data = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height).data;
                    const bounds = calculateOpaqueBounds(data, ctx.canvas.width, ctx.canvas.height, 255, {median});
                    console.timeEnd('Compute crop box from transparent pixels with median ' + median);

                    if (bounds) {
                        const { left, top, width, height } = bounds;
                        // Use crop box + 10px margin on each side
                        const initialCropbox = {
                            type: VEHICLE_BOX_ANNOTATION.type,
                            id: VEHICLE_BOX_ANNOTATION.id,
                            left: left / naturalWidth,
                            top: top / naturalHeight,
                            width: width / naturalWidth,
                            height: height / naturalHeight,
                        };

                        unsavedFactoid.manualRevision = 'correct';
                        imageProps.annotations = [initialCropbox];

                        setUnsavedFactoid({ ...unsavedFactoid });
                    }
                }

                if(!imageProps.width || !imageProps.height) {
                    const { naturalWidth, naturalHeight } = imageElement;
                    imageProps.width = naturalWidth;
                    imageProps.height = naturalHeight;
                    setUnsavedFactoid({ ... unsavedFactoid });
                }        
            }, 1);
        }

        return <div className="position-relative" style={{ width: '100%', height: 'calc(100% - 42px)' }}>
            <TopBarLayout>
                <div className={`p-2 ${unsavedChanges ? 'bg-light-warning' : ''}`}>
                    <a href={savedUrl} target="_blank" className={'d-flex align-items-center small my-1 ml-1'}>
                        <Icon icon={unsavedChanges ? "cloud_off" : "cloud_done"} level={unsavedChanges ? "danger" : "success"} className='mr-2' /> {savedUrl}
                    </a>

                    <div className={`d-flex align-items-center justify-content-between`} style={{minHeight: '32px'}}>
                        <FactoidImageCameraSelector value={camera} onChange={setCamera} />

                        {unsavedChanges ? <span className={'ml-3'}>
                            <BtnSeeDiff a={originalFactoid} b={unsavedFactoid} className={'mr-1'}/>

                            <IconButton
                                icon="save"
                                level={"success"}
                                fill={true}
                                onClick={() => handleSave(unsavedFactoid)}
                                disabled={isCalculating || !unsavedChanges}
                                className="mr-1"
                            >
                                Save changes
                            </IconButton>

                            <IconButton
                                icon="clear"
                                level={"danger"}
                                fill={true}
                                onClick={() => setUnsavedFactoid(_.cloneDeep(originalFactoid))}
                                disabled={isCalculating || !unsavedChanges}
                            >
                                Discard changes
                            </IconButton>
                        </span> : <span />}
                    </div>

                    <div className="text-left d-flex align-items-center">

                        {isCalculating && <div className="ml-2 text-center text-muted">Calculating crop box...</div>}

                        {error && <div className="alert alert-danger m-2">{error}</div>}
                    </div>
                </div>

                <ImageAnnotationsEditor key={'uploaded'} onImageLoad={checkVehicleBox}  initialWidth={imageProps.width} initialHeight={imageProps.height} value={editorValue} onChange={factoidAnnotationChange} knownAnnotations={[VEHICLE_BOX_ANNOTATION]} />
            </TopBarLayout>
        </div>
    };
}

export default SpecFactoidImageEditor;
